import React, { useState } from 'react';
import { Crown, Shield, Zap, TrendingUp, Check, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProPaywall = ({ feature = "this feature" }) => {
  const { isAuthenticated, login, createAuthHeaders, getToken } = useAuth();
  const [loading, setLoading] = useState(false);

  const handleUpgrade = async () => {
    if (!isAuthenticated) {
      login();
      return;
    }

    setLoading(true);
    try {
      const originUrl = window.location.origin;
      const res = await axios.post(`${API}/payment/create-checkout-session`, 
        { origin_url: originUrl },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      console.error('Checkout error:', err);
      if (err.response?.status === 400 && err.response?.data?.detail?.includes('already')) {
        window.location.reload();
      }
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    { icon: TrendingUp, text: "AI-Powered Chart Analysis" },
    { icon: Shield, text: "Weekly Institutional Macro Briefs" },
    { icon: Zap, text: "PDF Report Downloads" },
    { icon: Crown, text: "Priority Access to New Features" },
  ];

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4" data-testid="pro-paywall">
      <div className="max-w-lg w-full">
        {/* Pro Badge */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-[#C5A059] to-[#D4AF37] mb-6 shadow-lg shadow-[#C5A059]/20">
            <Crown size={36} className="text-black" />
          </div>
          <h2 className="font-manrope font-bold text-3xl text-white mb-3">
            Unlock <span className="text-[#C5A059]">DigitalGoldGang Pro</span>
          </h2>
          <p className="text-gray-400 text-base">
            Get full access to {feature} and all premium tools.
          </p>
        </div>

        {/* Pricing Card */}
        <div className="bg-gradient-to-b from-[#151515] to-[#0D0D0D] border border-[#C5A059]/30 rounded-2xl p-8 mb-6">
          {/* Price */}
          <div className="text-center mb-6">
            <div className="flex items-baseline justify-center gap-1">
              <span className="text-[#C5A059] text-lg">$</span>
              <span className="text-white font-bold text-5xl font-manrope">10</span>
              <span className="text-gray-500 text-base">/month</span>
            </div>
            <p className="text-gray-500 mt-1 text-sm">Cancel anytime</p>
          </div>

          {/* Benefits */}
          <ul className="space-y-4 mb-8">
            {benefits.map((b, i) => (
              <li key={i} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-[#C5A059]/10 flex items-center justify-center flex-shrink-0">
                  <Check size={16} className="text-[#C5A059]" />
                </div>
                <span className="text-gray-300 text-sm">{b.text}</span>
              </li>
            ))}
          </ul>

          {/* CTA */}
          <Button
            onClick={handleUpgrade}
            disabled={loading}
            className="w-full py-6 text-base font-bold bg-gradient-to-r from-[#C5A059] to-[#D4AF37] text-black hover:opacity-90 transition-opacity rounded-xl"
            data-testid="upgrade-pro-btn"
          >
            {loading ? (
              <><Loader2 size={18} className="animate-spin mr-2" /> Processing...</>
            ) : !isAuthenticated ? (
              "Sign in to Upgrade"
            ) : (
              "Upgrade to Pro — $10/mo"
            )}
          </Button>
        </div>

        <p className="text-center text-xs text-gray-600">
          Secure payment via Stripe. Instant access after payment.
        </p>
      </div>
    </div>
  );
};

export default ProPaywall;
